import React from 'react';
import { connect } from 'react-redux';
import { Entity, ctx as L20NContext } from '@sketchpixy/rubix/lib/L20n';
import {
  FormControl
} from '@sketchpixy/rubix';
import { Table, TableBody, TableCell, TableHead, TableRow, Paper, IconButton, Button } from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import DeleteIcon from '@material-ui/icons/RemoveCircleOutline';
import ShowIcon from '@material-ui/icons/Visibility';
import HideIcon from '@material-ui/icons/VisibilityOff';
import _ from 'lodash';
import * as StandardDevicesAPI from '../../../_config/standardDevicesAPI';
import * as UtilsActions from '../../../redux/actions/utils.actions';
import * as ModalActions from '../../../redux/actions/modal.actions';
import { getPreferredEncoders, togglePreferredEncoder } from '../../../_config/utils';
import InfoSection from '../../InfoSection/InfoSection.jsx';
import MDButton from '../../MDButton/MDButton.jsx';
import BasicModal from '../../Modals/BasicModal.jsx';

@connect(state => ({ userToken: state.user.token }))
class CompanyEncodersOperationalSection extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      encoders: [],
      filteredEncoders: [],
      searchQuery: '',
      isLoading: false,
      error: null,
      preferredEncoders: [],
      newEncoderName: '',
      createError: null,
      initCodes: {},
    };
  }

  async componentDidMount() {
    this.setState({ preferredEncoders: getPreferredEncoders() });
    await this.onFetchEncoders();
  }

  async onFetchEncoders() {
    const { dispatch } = this.props;
    this.setState({ isLoading: true, error: null });
    try {
      dispatch(UtilsActions.setSpinnerVisibile(true));
      const response = await StandardDevicesAPI.fetchCompanyEncoders();
      const encoders = response.data || [];
      this.setState({ 
        encoders, 
        filteredEncoders: encoders,
        isLoading: false,
        initCodes: {},
      });
      dispatch(UtilsActions.setSpinnerVisibile(false));
    } catch (error) {
      this.setState({ 
        error: error.message || 'Failed to fetch encoders',
        isLoading: false 
      });
      dispatch(UtilsActions.setSpinnerVisibile(false));
    }
  }

  onHandleSearch(query) {
    const { encoders } = this.state;
    const searchQuery = query.toLowerCase();
    
    const filteredEncoders = _.filter(encoders, (encoder) => {
      return (
        (encoder.name && encoder.name.toLowerCase().includes(searchQuery)) ||
        (encoder.description && encoder.description.toLowerCase().includes(searchQuery)) ||
        (encoder.deviceHwId && encoder.deviceHwId.toLowerCase().includes(searchQuery)) ||
        (encoder.encoderId && encoder.encoderId.toString().includes(searchQuery))
      );
    });

    this.setState({ searchQuery: query, filteredEncoders });
  }

  onTogglePreferred(encoderId) {
    const updated = togglePreferredEncoder(encoderId);
    this.setState({ preferredEncoders: updated });
  }

  async onToggleInitCodeVisibility(encoderId) {
    const { dispatch } = this.props;
    const { initCodes } = this.state;
    const current = initCodes[encoderId] || {};

    if (current.visible) {
      this.setState({
        initCodes: {
          ...initCodes,
          [encoderId]: { ...current, visible: false },
        },
      });
      return;
    }

    if (current.value) {
      this.setState({
        initCodes: {
          ...initCodes,
          [encoderId]: { ...current, visible: true },
        },
      });
      return;
    }

    this.setState({
      initCodes: {
        ...initCodes,
        [encoderId]: { ...current, loading: true, visible: true, error: null },
      },
    });

    try {
      const response = await StandardDevicesAPI.fetchEncoderInitCode(encoderId);
      const value = typeof response.data === 'string'
        ? response.data
        : (response.data && (response.data.initCode || response.data.value)) || '-';
      this.setState({
        initCodes: {
          ...this.state.initCodes,
          [encoderId]: { loading: false, visible: true, value, error: null },
        },
      });
    } catch (error) {
      this.setState({
        initCodes: {
          ...this.state.initCodes,
          [encoderId]: { loading: false, visible: false, value: null, error: error.message || 'error' },
        },
      });
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="initCodeFetchError" /></h6>),
        },
      }));
    }
  }

  showNewEncoderModal() {
    this.setState({ newEncoderName: '', createError: null });
    this.newEncoderModal.open();
  }

  showDeleteEncoderModal(encoder) {
    const { dispatch } = this.props;
    dispatch(ModalActions.showModal({
      modalType: 'CONFIRM_TO_CONTINUE_MODAL',
      modalProps: {
        title: 'confirmBeforeContinue',
        body: <Entity entity="deleteEncoderMessage" data={{ name: encoder.name || encoder.encoderId }} />,
        onConfirmText: <Entity entity="confirm" />,
        onConfirm: () => this.onDeleteEncoder(encoder),
        onCancelText: <Entity entity="cancel" />,
        onCancel: () => dispatch(ModalActions.hideModal()),
      },
    }));
  }

  async onDeleteEncoder(encoder) {
    const { dispatch } = this.props;
    const { preferredEncoders } = this.state;

    try {
      dispatch(UtilsActions.setSpinnerVisibile(true));
      await StandardDevicesAPI.deleteCompanyEncoder(encoder.encoderId);
      if (preferredEncoders.includes(encoder.encoderId)) {
        const updated = togglePreferredEncoder(encoder.encoderId);
        this.setState({ preferredEncoders: updated });
      }
      dispatch(ModalActions.hideModal());
      await this.onFetchEncoders();
      dispatch(UtilsActions.setSpinnerVisibile(false));
      dispatch(ModalActions.showModal({
        modalType: 'SUCCESS_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="encoderDeleteSuccess" /></h6>),
        },
      }));
    } catch (error) {
      dispatch(UtilsActions.setSpinnerVisibile(false));
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="encoderDeleteError" /></h6>),
        },
      }));
    }
  }

  async onCreateEncoder() {
    const { dispatch, userToken } = this.props;
    const { newEncoderName } = this.state;
    const trimmedName = newEncoderName.trim();

    if (!trimmedName) {
      this.setState({ createError: 'required' });
      return;
    }

    const payload = {
      name: trimmedName,
      description: userToken.plantName,
      slaveMode: 'Enabled',
      autoMode: 'Disabled',
      deviceType: 'SoftwareEncoder',
      configType: 'File',
      initialized: true,
    };

    try {
      dispatch(UtilsActions.setSpinnerVisibile(true));
      await StandardDevicesAPI.createCompanyEncoder(payload);
      dispatch(UtilsActions.setSpinnerVisibile(false));
      this.newEncoderModal.close();
      this.setState({ newEncoderName: '', createError: null });
      await this.onFetchEncoders();
      dispatch(ModalActions.showModal({
        modalType: 'SUCCESS_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="settingUpdateSuccessfully" /></h6>),
        },
      }));
    } catch (error) {
      dispatch(UtilsActions.setSpinnerVisibile(false));
      dispatch(ModalActions.showModal({
        modalType: 'ERROR_ALERT',
        modalProps: {
          message: (<h6 className="snack-title"><Entity entity="settingUpdateError" /></h6>),
        },
      }));
    }
  }

  render() {
    const { filteredEncoders, searchQuery, error, newEncoderName, createError, initCodes } = this.state;

    return (
      <div style={{ paddingLeft: 20, paddingRight: 20, paddingBottom: 40 }}>
        <div style={{ marginBottom: 20 }}>
          <h4 style={{ marginBottom: 10, color: '#3f3f3f' }}>
            <Entity entity="companyEncodersDescription" />
          </h4>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 15 }}>
          <MDButton
            title={<Entity entity="createEncoder" />}
            onClick={() => this.showNewEncoderModal()}
            containerStyle={{ marginLeft: 0 }}
          />
        </div>

        <FormControl
          type="text"
          value={searchQuery}
          placeholder={L20NContext.getSync('searchEncoders')}
          className="form-control-rounded"
          size="small"
          style={{ width: '100%', borderRadius: 5, marginBottom: 20 }}
          onChange={e => this.onHandleSearch(e.target.value)}
        />

        {error && (
          <InfoSection
            title="error"
            description={error}
            gravity="error"
            containerStyle={{ marginBottom: 20 }}
          />
        )}

        {_.isEmpty(filteredEncoders) && !error && (
          <InfoSection
            title="noEncodersAvailable"
            description="noEncodersFoundDescription"
            gravity="info"
            containerStyle={{ marginBottom: 20 }}
          />
        )}

        {!_.isEmpty(filteredEncoders) && (
          <Paper elevation={2}>
            <Table style={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead style={{ backgroundColor: '#f5f5f5' }}>
                <TableRow>
                  <TableCell align="center" style={{ width: 100 }}>
                    <h5 style={{ margin: 0, fontWeight: 600 }}>
                      <Entity entity="preferred" />
                    </h5>
                  </TableCell>
                  <TableCell>
                    <h5 style={{ margin: 0, fontWeight: 600 }}>
                      <Entity entity="Id" />
                    </h5>
                  </TableCell>
                  <TableCell>
                    <h5 style={{ margin: 0, fontWeight: 600 }}>
                      <Entity entity="name" />
                    </h5>
                  </TableCell>
                  <TableCell>
                    <h5 style={{ margin: 0, fontWeight: 600 }}>
                      <Entity entity="serialNumber" />
                    </h5>
                  </TableCell>
                  <TableCell>
                    <h5 style={{ margin: 0, fontWeight: 600 }}>
                      <Entity entity="type" />
                    </h5>
                  </TableCell>
                  <TableCell>
                    <h5 style={{ margin: 0, fontWeight: 600 }}>
                      <Entity entity="initialized" />
                    </h5>
                  </TableCell>
                  <TableCell style={{ width: 200, maxWidth: 200 }}>
                    <h5 style={{ margin: 0, fontWeight: 600 }}>
                      <Entity entity="initCode" />
                    </h5>
                  </TableCell>
                  <TableCell align="center" style={{ width: 100 }}>
                    <h5 style={{ margin: 0, fontWeight: 600 }}>
                      <Entity entity="actions" />
                    </h5>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {_.map(filteredEncoders, (encoder) => (
                  <TableRow key={encoder.encoderId} hover>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => this.onTogglePreferred(encoder.encoderId)}
                        style={{ padding: 5 }}
                      >
                        {this.state.preferredEncoders.includes(encoder.encoderId) ? (
                          <StarIcon style={{ color: '#FFC107', fontSize: 28 }} />
                        ) : (
                          <StarBorderIcon style={{ color: '#9e9e9e', fontSize: 28 }} />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <h6 style={{ margin: 0 }}>{encoder.encoderId}</h6>
                    </TableCell>
                    <TableCell>
                      <h6 style={{ margin: 0 }}>{encoder.name || '-'}</h6>
                    </TableCell>
                    <TableCell>
                      <h6 style={{ margin: 0 }}>{encoder.deviceHwId || '-'}</h6>
                    </TableCell>
                    <TableCell>
                      <h6 style={{ margin: 0 }}>{encoder.deviceType || '-'}</h6>
                    </TableCell>
                    <TableCell>
                      <h6 style={{ margin: 0, color: encoder.initialized ? '#4caf50' : '#f44336', fontWeight: 600 }}>
                        {encoder.initialized ? <Entity entity="yes" /> : <Entity entity="no" />}
                      </h6>
                    </TableCell>
                    <TableCell style={{ width: 200, maxWidth: 200, overflow: 'hidden' }}>
                      {encoder.deviceType === 'SoftwareEncoder' && (
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', marginRight: 4 }}>
                            {initCodes[encoder.encoderId] && initCodes[encoder.encoderId].visible && (
                              <h6
                                style={{
                                  margin: 0,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                                title={initCodes[encoder.encoderId].loading ? '' : (initCodes[encoder.encoderId].value || '')}
                              >
                                {initCodes[encoder.encoderId].loading ? '...' : (initCodes[encoder.encoderId].value || '-')}
                              </h6>
                            )}
                          </div>
                          <IconButton
                            size="small"
                            onClick={() => this.onToggleInitCodeVisibility(encoder.encoderId)}
                            style={{ padding: 5, flexShrink: 0 }}
                            disabled={initCodes[encoder.encoderId] && initCodes[encoder.encoderId].loading}
                          >
                            {initCodes[encoder.encoderId] && initCodes[encoder.encoderId].visible ? (
                              <HideIcon style={{ color: '#9e9e9e', fontSize: 22 }} />
                            ) : (
                              <ShowIcon style={{ color: '#9e9e9e', fontSize: 22 }} />
                            )}
                          </IconButton>
                        </div>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {encoder.deviceType === 'SoftwareEncoder' && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Button
                            onClick={(event) => {
                              event.stopPropagation();
                              this.showDeleteEncoderModal(encoder);
                            }}
                            style={{ color: '#E53935', fontSize: 12, fontWeight: 'bold', textDecoration: 'underline' }}
                          >
                            <Entity entity="delete" />
                            <DeleteIcon style={{ fontSize: 15, marginLeft: 5 }} />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}

        <div style={{ marginTop: 20 }}>
          <h5 style={{ color: '#666', fontWeight: 400 }}>
            <Entity entity="totalEncoders" key={_.size(filteredEncoders)} data={{ count: _.size(filteredEncoders) }} />
          </h5>
        </div>

        <BasicModal
          ref={(m) => { this.newEncoderModal = m; }}
          title={<Entity entity="newEncoder" />}
          body={
            <div>
              <h5 style={{ marginBottom: 10 }}>
                <Entity entity="name" />
              </h5>
              <FormControl
                type="text"
                value={newEncoderName}
                placeholder={L20NContext.getSync('insertName')}
                className="form-control-custom"
                onChange={e => this.setState({ newEncoderName: e.target.value, createError: null })}
              />
              {createError && (
                <h6 style={{ color: '#f44336', margin: 0 }}>
                  <Entity entity="required" />
                </h6>
              )}
              <MDButton
                containerStyle={{ marginTop: 30 }}
                title={<Entity entity="createEncoder" />}
                onClick={() => this.onCreateEncoder()}
              />
            </div>
          }
        />
      </div>
    );
  }
}

export default CompanyEncodersOperationalSection;
